// BUILD-046 customer association: the "Not in the list? Open Pricing to add a customer if your Pricing role permits it." note below the Customer picker of the Assign task dialog.
//
// A customer is created in Pricing, not here. When the person cannot find one, the dialog says so in one of four ways, decided HERE and nowhere else:
//   link        : `<pricing address>/customers`, opened in a NEW tab by the component (so the Assign task dialog and everything typed in it stay open). Offered only when the
//                 server said the person has Pricing PRODUCT access (`GET /work/me` `canAddCustomerInPricing === true`, and `pricingCustomerAccess` is `allowed` or not stated) AND a
//                 valid http(s) Pricing address is configured (`config.pricingUrl`, from VITE_PRICING_URL). Product access is not verified create authority, so the words are
//                 conditional ("if your Pricing role permits it") and Pricing enforces its own role when the link is followed. No role is read or named here;
//   text        : a plain note that the task can be assigned without a customer and that a person whose Pricing role permits it can add the customer. Used when the server said no, said
//                 nothing (an older server), or no usable address is configured;
//   unavailable : a plain note that Pricing access could not be checked right now (`pricingCustomerAccess` is `unavailable`, or a value this code does not know). A check that
//                 could not be made is NOT shown as "no access", and it never makes a link. The task can still be assigned without a customer;
//   none        : nothing at all - the Customer picker itself is not offered (customers not enabled).
// The only inputs are those values (and whether customers are offered). Availability never comes from a person's name, a role string or a hard-coded list, and the
// flag is advice for the UI: it grants nothing, Pricing enforces its own access when the link is followed.
//
// Pure module: no React, no DOM, no I/O. Erasable TypeScript, no imports.

/**
 * The sentence a link carries. It states a condition, not a permission: the platform's answer behind the link is product access to Pricing, which is NOT verified authority to create a
 * customer. Pricing decides that itself, by role, when the link is followed (Builder 2026-10-25, "Pricing advice").
 */
export const PRICING_LINK_TEXT = 'Not in the list? Open Pricing to add a customer if your Pricing role permits it.';
/** The plain note when no link is offered (the server said no, said nothing, or no Pricing address is configured). It does not say who can create a customer. */
export const PRICING_PLAIN_TEXT = 'Not in the list? You can assign the task without a customer. A person whose Pricing role permits it can add the customer in Pricing, and you can link it later from Edit.';
/** The plain note when Pricing access could not be checked (not the same as "no access"). */
export const PRICING_UNAVAILABLE_TEXT = 'Pricing access could not be checked right now. You can still assign this task without a customer.';
/** The control that asks the directory again after a visit to Pricing. */
export const PRICING_SEARCH_AGAIN_LABEL = 'Search again';
/** Where the Customers page lives under the configured Pricing address. */
export const PRICING_CUSTOMERS_PATH = '/customers';

export type PricingNote = { kind: 'link'; href: string } | { kind: 'text' } | { kind: 'unavailable' } | { kind: 'none' };

export interface PricingLinkInput {
  /** `GET /work/me` `canAddCustomerInPricing`. Anything other than `true` (false, absent, an older server) means no link. */
  canAddCustomerInPricing?: unknown;
  /**
   * `GET /work/me` `pricingCustomerAccess`. Absent: decided by `canAddCustomerInPricing` alone, as before. `allowed`: a link when the flag above is also `true` and the address is usable.
   * `not-allowed` or `not-offered`: the plain note. `unavailable`, or any other value: the "could not be checked" note, never a link.
   */
  pricingCustomerAccess?: unknown;
  /** `config.pricingUrl` (VITE_PRICING_URL): the base address of the Pricing app, or '' when not configured. */
  pricingUrl?: unknown;
  /** Whether the Customer picker is offered at all. Defaults to true; false means the note is not shown. */
  customersEnabled?: unknown;
}

/**
 * The Customers page of the configured Pricing app, or null when the address is not a usable http(s) address. Anything else (a missing value, another scheme such as
 * javascript:, an address with a user name or password in it, text that is not an address) is not usable. A query string or fragment on the configured address is
 * dropped, and the path keeps whatever sub-path the app lives under: `https://pricing.example.com/app/` becomes `https://pricing.example.com/app/customers`.
 */
export function pricingCustomersHref(pricingUrl: unknown): string | null {
  if (typeof pricingUrl !== 'string') return null;
  const raw = pricingUrl.trim();
  if (raw === '') return null;
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return null;
  }
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
  if (url.hostname === '' || url.username !== '' || url.password !== '') return null;
  const path = url.pathname.replace(/\/+$/, '');
  return `${url.origin}${path}${PRICING_CUSTOMERS_PATH}`;
}

/** Link, plain text, a "could not be checked" note or nothing for the note below the Customer picker. */
export function decidePricingNote(input: PricingLinkInput): PricingNote {
  if (input === null || typeof input !== 'object') return { kind: 'text' };
  if (input.customersEnabled === false) return { kind: 'none' };
  const access = input.pricingCustomerAccess;
  if (access === 'not-allowed' || access === 'not-offered') return { kind: 'text' };
  if (access !== undefined && access !== 'allowed') return { kind: 'unavailable' };
  if (input.canAddCustomerInPricing === true) {
    const href = pricingCustomersHref(input.pricingUrl);
    if (href !== null) return { kind: 'link', href };
  }
  return { kind: 'text' };
}

/**
 * After the person followed the Pricing link and comes back to this window, the directory is asked again - but only then, only while no customer is chosen and only when
 * there is text to search for, so ordinary switching between windows never re-runs a search, disturbs a chosen customer or moves focus for nothing. (With nothing typed the
 * visible "Search again" button, and typing itself, still work.)
 */
export function shouldSearchAgainOnReturn(state: { followedPricingLink: boolean; customerChosen: boolean; hasSearchText: boolean }): boolean {
  return state.followedPricingLink === true && state.customerChosen !== true && state.hasSearchText === true;
}
